import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { LanguagePicker } from "./LanguagePicker";
import { t, useLang } from "../lib/i18n";

export function Footer() {
  useLang();
  const { user, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/mailing-list", { email });
      toast.success("You're on the list");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelPlus = async () => {
    if (!window.confirm("Cancel Reads+? You'll keep access until your current period ends. No refund is issued.")) return;
    try {
      const { data } = await api.post("/me/unsubscribe");
      toast.success(data.message || "Reads+ will end at the end of your period.");
      await refresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const isAuthed = user && typeof user === "object";

  return (
    <footer className="border-t border-[rgba(10,10,10,0.10)] bg-[#FAF5E6] mt-20" data-testid="site-footer">
      {/* Site-wide founder's mission strip */}
      <div className="border-b border-[rgba(10,10,10,0.10)] bg-[#0A0A0A] py-8" data-testid="footer-mission">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="font-mono uppercase tracking-[0.3em] text-[9px] text-[#F2EAD3]/55 mb-2">{t("footer.mission_label")}</div>
          <blockquote className="font-heading italic text-base md:text-lg text-[#F2EAD3] leading-snug" data-testid="footer-mission-quote">
            {t("footer.mission_quote")}
          </blockquote>
          <div className="font-mono uppercase tracking-widest text-[9px] text-[#F2EAD3]/60 mt-2">{t("footer.founder")}</div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="font-heading text-2xl text-[#0A0A0A] tracking-tight">reads</div>
          <p className="text-[#4A4030] text-sm leading-relaxed mt-3 max-w-sm">
            {t("footer.tagline")}
          </p>
          <form onSubmit={subscribe} className="mt-6 max-w-sm" data-testid="mailing-list-form">
            <label className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] block mb-2">{t("footer.join")}</label>
            <div className="flex gap-2">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.mailing_placeholder")}
                className="flex-1 bg-[#F2EAD3] border border-[rgba(10,10,10,0.12)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                data-testid="mailing-list-input"
              />
              <button type="submit" disabled={submitting}
                className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm disabled:opacity-50 transition-colors"
                data-testid="mailing-list-submit">
                {submitting ? "…" : t("footer.subscribe")}
              </button>
            </div>
          </form>
        </div>

        <div className="md:col-span-2">
          <div className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] mb-4">{t("footer.product")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/discover" className="text-[#0A0A0A] hover:underline">{t("nav.discover")}</Link></li>
            <li><Link to="/subscribe" className="text-[#0A0A0A] hover:underline">{t("nav.plus")}</Link></li>
            <li><Link to="/library" className="text-[#0A0A0A] hover:underline">{t("nav.library")}</Link></li>
            {isAuthed && <li><Link to="/recommendations" className="text-[#0A0A0A] hover:underline">{t("nav.recommendations")}</Link></li>}
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] mb-4">{t("footer.support")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/blog" className="text-[#0A0A0A] hover:underline" data-testid="footer-blog">Reading tips</Link></li>
            <li><Link to="/faq" className="text-[#0A0A0A] hover:underline" data-testid="footer-faq">{t("footer.faq")}</Link></li>
            <li><Link to="/help" className="text-[#0A0A0A] hover:underline" data-testid="footer-help">{t("footer.help")}</Link></li>
            <li><Link to="/contact" className="text-[#0A0A0A] hover:underline" data-testid="footer-contact">{t("footer.contact")}</Link></li>
            {isAuthed && user.is_plus && (
              <li>
                <button onClick={cancelPlus} className="text-[#4A4030] hover:underline text-sm" data-testid="footer-unsubscribe">
                  {t("footer.cancel_plus")}
                </button>
              </li>
            )}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] mb-4">{t("footer.language")}</div>
          <LanguagePicker />
          <Link to="/contact" className="inline-block mt-4 text-[#0A0A0A] hover:underline text-sm" data-testid="footer-connect">{t("footer.message_team")}</Link>

          <div className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] mt-6 mb-2">{t("home.follow_us")}</div>
          <div className="flex flex-col gap-1.5">
            <a
              href="https://instagram.com/readscoteam"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#0A0A0A] hover:underline text-sm"
              data-testid="footer-instagram"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="5" ry="5"/>
                <path d="M16.5 7.5v.001"/>
                <circle cx="12" cy="12" r="4"/>
              </svg>
              @readscoteam
            </a>
            <a
              href="https://x.com/ReadsCoOfficial"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#0A0A0A] hover:underline text-sm"
              data-testid="footer-x"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2H21l-6.52 7.45L22 22h-6.828l-4.77-6.234L4.8 22H2.043l6.98-7.977L2 2h6.914l4.32 5.71L18.244 2Zm-2.393 18.35h1.594L7.24 3.556H5.53L15.85 20.35Z"/>
              </svg>
              @ReadsCoOfficial
            </a>
            <a
              href="https://www.tiktok.com/@Readscoofficial"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#0A0A0A] hover:underline text-sm"
              data-testid="footer-tiktok"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.01a8.16 8.16 0 0 0 4.77 1.53V7.11a4.85 4.85 0 0 1-1.84-.42Z"/>
              </svg>
              @Readscoofficial
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-[rgba(10,10,10,0.08)] py-5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center justify-between gap-3 font-mono uppercase tracking-widest text-[10px] text-[#4A4030]">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="footer-legal-links">
            <Link to="/legal/privacidad" className="hover:underline hover:text-[#0A0A0A]" data-testid="footer-privacy">{t("legal.privacy")}</Link>
            <span aria-hidden className="opacity-40">·</span>
            <Link to="/legal/condiciones" className="hover:underline hover:text-[#0A0A0A]" data-testid="footer-terms">{t("legal.terms")}</Link>
            <span aria-hidden className="opacity-40">·</span>
            <Link to="/legal/aviso-legal" className="hover:underline hover:text-[#0A0A0A]" data-testid="footer-legal-notice">{t("legal.notice")}</Link>
            <span aria-hidden className="opacity-40">·</span>
            <Link to="/legal/cookies" className="hover:underline hover:text-[#0A0A0A]" data-testid="footer-cookies">{t("legal.cookies")}</Link>
          </div>
          <div>© {new Date().getFullYear()} reads — A modern reading journal</div>
        </div>
      </div>
    </footer>
  );
}
